import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Row, Col, Collapse } from "react-bootstrap";
import { FaTrash, FaChevronDown, FaChevronUp, FaPlus, FaTimes, FaCheck } from "react-icons/fa";
import { FieldArray, useFormikContext, Form as FormikForm } from "formik";
import FormikInput from "../formik/FormikInput";
import FormikRadioBox from "../formik/FormikRadioBox";
import SelectSearchDropDown from "../SelectSearchDropDown";
import UserMultiSelect from "src/business-components/UserMultiSelect";
import { getTaskListAPI, TaskUpsertAPI } from "src/api/ScheduleTaskApi";
import SelectDropDown from "../SelectDropDown";
import FormikTextarea from "../formik/FormikTextarea";
import FormikTextareaFieldArray from "../formik/FormikTextareaFieldArray";
import {ErrorMessage } from 'formik';

const daysOfWeek = ["M", "T", "W", "TH", "F"];
const weekdayHeader = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const defaultSubtask = {
  subTaskName: "",
  assignTo: "Self",
  users: [],
  frequency: "Daily",
  selectedDays: [...daysOfWeek],
  selectedMonthDates: [],
  startDate: "",
  initiationType: "Dependency",
  time: "",
  duration: 0,
  durationUnit: "Hours",
};

const formatISODate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseISODate = (iso) => {
  const [y, m, d] = iso.split("-");
  return new Date(Number(y), Number(m) - 1, Number(d));
};

const getDaysInMonth = (year, monthIndex) => {
  return new Date(year, monthIndex + 1, 0).getDate();
};

const TaskForm = (props) => {
  const { show, handleClose, editSelectedTask } = props;
  const formik = useFormikContext();
  const { values, setFieldValue } = formik;
  const [expandedStates, setExpandedStates] = useState([]);
  const [isTaskUpsert, setIsTaskUpsert] = useState(false);
  const [taskUpsertError, setTaskUpsertError] = useState("");
  // calendarMonths left for compatibility but monthly UI no longer uses specific month
  const [calendarMonths, setCalendarMonths] = useState([]);

  useEffect(() => {
    if (show && values?.subTasks?.length > 0) {
      const states = values.subTasks.map((subTask) =>
        editSelectedTask?.toString() === subTask._id?.toString() ? true : false
      );
      setExpandedStates(states);
      setCalendarMonths((prev) => {
        const cp = [...prev];
        for (let i = 0; i < values.subTasks.length; i++) {
          if (!cp[i]) cp[i] = new Date();
        }
        return cp;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    setCalendarMonths((prev) => {
      const cp = [...prev];
      const needed = values.subTasks?.length || 0;
      while (cp.length < needed) cp.push(new Date());
      while (cp.length > needed) cp.pop();
      return cp;
    });
  }, [values.subTasks?.length]);

  const toggleExpand = (index) => {
    const updated = [...expandedStates];
    updated[index] = !updated[index];
    setExpandedStates(updated);
  };

  const handleFrequencyChange = (index, value) => {
    setFieldValue(`subTasks[${index}].frequency`, value);

    if (value === "Daily") {
      setFieldValue(`subTasks[${index}].selectedDays`, [...daysOfWeek]);
      setFieldValue(`subTasks[${index}].selectedMonthDates`, []);
    } else if (value === "Weekly") {
      setFieldValue(`subTasks[${index}].selectedDays`, []);
      setFieldValue(`subTasks[${index}].selectedMonthDates`, []);
    } else if (value === "Custom") {
      setFieldValue(`subTasks[${index}].selectedDays`, []);
      setFieldValue(`subTasks[${index}].selectedMonthDates`, []);
    } else if (value === "Monthly") {
      // monthly stores day numbers (1..31)
      const existing = values?.subTasks?.[index]?.selectedMonthDates || [];
      if (!existing || existing.length === 0) {
        setFieldValue(`subTasks[${index}].selectedMonthDates`, []);
      }
      setFieldValue(`subTasks[${index}].selectedDays`, []);
    }
  };

  const handleDaySelection = (index, day) => {
    const selected = values.subTasks[index].selectedDays || [];
    const freq = values.subTasks[index].frequency;

    let newDays = [...selected];
    let newFreq = freq;

    if (freq === "Daily") return;

    if (freq === "Weekly") {
      newDays = [day];
    } else if (freq === "Custom") {
      newDays = selected.includes(day)
        ? selected.filter((d) => d !== day)
        : [...selected, day];
      if (newDays.length === daysOfWeek.length) {
        newFreq = "Daily";
        newDays = [...daysOfWeek];
      }
    }

    setFieldValue(`subTasks[${index}].selectedDays`, newDays);
    setFieldValue(`subTasks[${index}].frequency`, newFreq);
  };

  // Monthly: toggle a day number (1..31). Store numbers sorted ascending.
  const handleMonthDayToggle = (index, dayNumber) => {
    const selected = values.subTasks[index].selectedMonthDates || [];
    const exists = selected.includes(String(dayNumber));
    const newSelected = exists
      ? selected.filter((n) => n !== String(dayNumber))
      : [...selected, String(dayNumber)];
    // sort numeric ascending
    newSelected.sort((a, b) => a - b);
    setFieldValue(`subTasks[${index}].selectedMonthDates`, newSelected);
  };

  const renderCalendar = (index) => {
    // render simple 1..31 grid (no week headers, no month nav)
    const selectedSet = new Set(values.subTasks[index].selectedMonthDates || []);

    const numbers = Array.from({ length: 31 }, (_, i) => i + 1);

    return (
      <div className="border rounded p-2 bg-white shadow-sm" style={{ maxWidth: 360 }}>
        <div className="mb-2">
          <strong>Days 1–31</strong>
        </div>

        {/* no weekday header */}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {numbers.map((n) => {
            const isSelected = selectedSet.has(n) || selectedSet.has(String(n));
            const todayDayNumber = new Date().getDate();
            const isToday = n === todayDayNumber;
            return (
              <button
                key={n}
                type="button"
                onClick={() => handleMonthDayToggle(index, n)}
                className={`btn btn-sm ${isSelected ? "btn-primary" : "btn-outline-secondary"}`}
                style={{
                  width: 40,
                  height: 36,
                  padding: 0,
                  borderRadius: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isToday ? "0 0 0 2px rgba(100,149,237,0.12)" : "none",
                }}
                title={`Day ${n}`}
              >
                <span style={{ fontSize: 12 }}>{n}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const handleToggleUpsert = async () => {
    setTaskUpsertError("");
    if (isTaskUpsert) {
      const name = (values?.taskName || "").trim();
      if (name) {
        try {
          const resp = await TaskUpsertAPI({
            taskId: values?._id,
            taskName: name,
          });
          setFieldValue("_id", null);
          setFieldValue("taskName", "");
        } catch (err) {
          console.warn("Task upsert failed:", err?.response?.data?.message);
          setTaskUpsertError(err?.response?.data?.message || "Failed to upsert task");
        } finally {
          setIsTaskUpsert(false);
        }
      } else {
        setIsTaskUpsert(false);
      }
    } else {
      setIsTaskUpsert(true);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="md" centered backdrop="static">
      <FormikForm autoComplete="disabled">
        <Modal.Header closeButton>
          <div className="modal-title custom-modal-title fw-bold text-primary font-24">
            {(values.subTasks.length > 0 && values.subTasks[0]._id) ? "Update" : "Add"} Task
          </div>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <Form.Group className="mb-3">
            <div className="d-flex align-items-center justify-content-between gap-2">
              <div style={{ flex: 1 }}>
                {isTaskUpsert ? (
                  <FormikInput
                    type="text"
                    name="taskName"
                    className="font-16 form-control"
                    placeholder="Enter the project name"
                  />
                ) : (
                  // <SelectSearchDropDown
                  //   name="taskId"
                  //   isSearchLocal={true}
                  //   placeholder="Select Project"
                  //   displayKeyPath={['taskName']}
                  //   valueKeyPath="_id"
                  //   defaultValueFetchByKeyPath="_id"
                  //   whereClauseKeysPaths={['taskName']}
                  //   api={getTaskListAPI}
                  //   apiCallAddonData={{}}
                  //   value={values._id}
                  //   onChange={(item) => {
                  //     values._id = item?._id || null;
                  //     values.taskName = item?.taskName || "";
                  //   }}
                  //   pageLength={25}
                  // />
                  <>
                    <SelectDropDown
                      api={getTaskListAPI}
                      displayKey="taskName"
                      valueKey="_id"
                      value={values._id}
                      onChange={
                        (value, item) => {
                          // values._id = value || null;
                          setFieldValue('_id', value || null);
                          setFieldValue('taskName', item.label || '');
                        }
                      }
                      placeholder="Select Project"
                    />
                    <ErrorMessage
                      component="div"
                      className="text-danger font-12"
                      name={"taskName"}
                    />
                  </>
                )}
              </div>

              <div style={{ marginLeft: 8 }}>
                <Button
                  variant={isTaskUpsert ? "outline-danger" : "primary"}
                  onClick={handleToggleUpsert}
                  className="d-inline-flex align-items-center justify-content-center p-2"
                  title={isTaskUpsert ? (values.taskName ? "Create project" : "Cancel") : "Create new project"}
                  style={{ minWidth: 40, minHeight: 40 }}
                >
                  {isTaskUpsert ? (
                    values.taskName && values.taskName.trim().length > 0 ? (
                      <FaCheck />
                    ) : (
                      <FaTimes />
                    )
                  ) : (
                    <FaPlus />
                  )}
                </Button>
              </div>
            </div>
            {taskUpsertError && <div className="text-danger mt-2">{taskUpsertError}</div>}
          </Form.Group>

          <FieldArray name="subTasks">
            {({ push, remove }) => (
              <>
                {values.subTasks.map((task, index) => (
                  <div className="mb-3 border rounded" key={index}>
                    <div className="d-flex justify-content-between align-items-center px-16 bg-light">
                      <strong>Task {index + 1}</strong>
                      <div className="d-flex align-items-center">
                        <Button variant="link" onClick={() => toggleExpand(index)} className="text-dark">
                          {expandedStates[index] ? <FaChevronUp /> : <FaChevronDown />}
                        </Button>
                        {values.subTasks.length > 1 && (
                          <Button className="border-0 d-flex bg-none text-danger" size="sm" onClick={() => remove(index)}>
                            <FaTrash />
                          </Button>
                        )}
                      </div>
                    </div>

                    <Collapse in={expandedStates[index]}>
                      <div className="p-3">
                        <Row className="mb-2">
                          <Col>
                            {/* <FormikInput
                              type="text"
                              name={`subTasks[${index}].subTaskName`}
                              className="font-16 form-control"
                              placeholder="Enter the task name"
                            /> */}
                            <FormikTextareaFieldArray
                              name={`subTasks[${index}].subTaskName`}
                              className="font-16 form-control"
                              rows={4}
                              cols={50}
                              placeholder="Enter the task name"
                            />
                          </Col>
                        </Row>

                        <Row className="mb-2 mt-3">
                          <Form.Label>Assign To</Form.Label>
                          <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center gap-1">
                              <FormikRadioBox
                                id={`assignTo-self-${index}`}
                                value="Self"
                                name={`subTasks[${index}].assignTo`}
                                label="Self"
                                checked={task.assignTo === "Self"}
                                className="form-check-input mt-0"
                              />
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              <FormikRadioBox
                                id={`assignTo-other-${index}`}
                                value="Other"
                                name={`subTasks[${index}].assignTo`}
                                label="Other"
                                checked={task.assignTo === "Other"}
                                className="form-check-input mt-0"
                              />
                            </div>
                          </div>
                          <div className="mt-3">
                            {task.assignTo === "Other" && (
                              <UserMultiSelect
                                className="form-control p-0 border-0 heightsetAuto"
                                name={`subTasks[${index}].users`}
                                value={task.users}
                                onChange={(selected) => setFieldValue(`subTasks[${index}].users`, selected)}
                              />
                            )}
                          </div>
                        </Row>

                        <Row className="mb-2 mt-3">
                          <Form.Label>Frequency</Form.Label>
                          <div className="d-flex align-items-center gap-3">
                            {["Daily", "Weekly", "Custom", "Monthly"].map((freq) => (
                              <div className="d-flex align-items-center gap-1" key={freq}>
                                <FormikRadioBox
                                  id={`frequency-${freq}-${index}`}
                                  value={freq}
                                  name={`subTasks[${index}].frequency`}
                                  label={freq}
                                  checked={task.frequency === freq}
                                  className="form-check-input mt-0"
                                  onClick={() => {
                                    handleFrequencyChange(index, freq);
                                  }}
                                />
                              </div>
                            ))}
                          </div>

                          <div className="mt-3">
                            {/* Daily/Weekly/Custom days of week buttons */}
                            {["Daily", "Weekly", "Custom"].includes(task.frequency) && (
                              <div className="d-flex align-items-center gap-3">
                                {daysOfWeek.map((day) => (
                                  <Button
                                    key={day}
                                    variant={task.selectedDays && task.selectedDays.includes(day) ? "primary" : "outline-primary"}
                                    onClick={() => handleDaySelection(index, day)}
                                    className="me-1 mb-1 rounded-pill w-32-px h-32-px font-12 p-0"
                                  >
                                    {day}
                                  </Button>
                                ))}
                              </div>
                            )}

                            {/* Monthly: 1..31 grid */}
                            {task.frequency === "Monthly" && (
                              <div className="mt-2">
                                {renderCalendar(index)}
                                <div className="mt-2 small text-secondary">
                                  {task.selectedMonthDates && task.selectedMonthDates.length > 0
                                    ? `Selected: ${task.selectedMonthDates.join(", ")}`
                                    : "No dates selected"}
                                </div>
                              </div>
                            )}
                          </div>
                        </Row>

                        <Row className="mb-2 mt-3">
                          <Form.Label>Initiation Type</Form.Label>
                          <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center gap-1">
                              <FormikRadioBox
                                id={`initiation-dependency-${index}`}
                                value="Dependency"
                                name={`subTasks[${index}].initiationType`}
                                label="Any Time"
                                className="form-check-input mt-0"
                                checked={task.initiationType === "Dependency"}
                              />
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              <FormikRadioBox
                                id={`initiation-fixed-${index}`}
                                value="Fixed"
                                name={`subTasks[${index}].initiationType`}
                                label="Fixed Time"
                                className="form-check-input mt-0"
                                checked={task.initiationType === "Fixed"}
                              />
                            </div>
                            {task.initiationType === "Fixed" && (
                              <FormikInput
                                type="time"
                                name={`subTasks[${index}].time`}
                                className="form-control font-14 w-160-px"
                                onFocus={(e) => e.target.showPicker && e.target.showPicker()}
                              />
                            )}
                          </div>
                        </Row>
                      </div>
                    </Collapse>
                  </div>
                ))}

                {/* <div className="mb-3">
                  <Button
                    variant="link"
                    onClick={() => {
                      push(defaultSubtask);
                      setExpandedStates((prev) => [...prev, true]);
                    }}
                    className="text-decoration-none"
                    style={{ color: "blueviolet" }}
                  >
                    + Add Another Task
                  </Button>
                </div> */}
              </>
            )}
          </FieldArray>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Save
          </Button>
        </Modal.Footer>
      </FormikForm>
    </Modal>
  );
};

export default TaskForm;